import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import moment from 'moment';

import { Box, Button, Paper, Stack, Typography } from '@mui/material';
import PhotoFilterOutlinedIcon from '@mui/icons-material/PhotoFilterOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import { TableRowsOutlined as TableRowsOutlinedIcon } from '@mui/icons-material';

import { SCUIDateRangerV2, SCUILoadingLinear, SCUINoRecordComponent } from '../../../atomic/atoms';
import SCUIDownloadButton from '../../../atomic/atoms/SCUIDownloadButton/SCUIDownloadButton';
import { SCUIBreadCrumb, SCUICustomDataGrid } from '../../../atomic/molecules';
import { SCUIContent } from '../../../atomic/organisms';

import { PendingRetouchTransfersQuery } from '../../../services/studio/StudioQuery/PendingRetouchTransfers/PendingRetouchTransfersQuery.services';
import { downloadExcel } from '../../../utils/excelFileHelper';

const CONTROL_HEIGHT = 48;
const DATE_FORMAT = 'YYYY-MM-DD';

type PendingRetouchTransfersRow = {
    id: string;
    fileTransferRequestDetailId: number;
    fileTransferRequestId: number;
    barcode: number;
    specialCode: string;
    colorCode: string;
    keyCode: string;
    originalFilename: string;
    storedFilename: string;
    transferDate: string;
    createdBy: string;
};

type AppliedFilters = {
    TransferDateStart: string;
    TransferDateEnd: string;
};

const pick = (item: Record<string, unknown>, camel: string, pascal: string) =>
    (item[camel] ?? item[pascal] ?? '') as string | number | null;

const formatDisplayDate = (value: unknown): string => {
    if (value == null || value === '') {
        return '';
    }

    const date = value instanceof Date ? value : new Date(String(value));
    if (Number.isNaN(date.getTime())) {
        return String(value);
    }

    const pad = (n: number) => String(n).padStart(2, '0');
    return `${pad(date.getDate())}-${pad(date.getMonth() + 1)}-${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
};

const getDefaultDateRange = (): { start: string; end: string } => {
    const end = moment().startOf('day');
    const start = end.clone().subtract(1, 'month');
    return {
        start: start.format(DATE_FORMAT),
        end: end.format(DATE_FORMAT),
    };
};

const isDateRangeWithinOneMonth = (startDate: string, endDate: string): boolean => {
    if (!startDate || !endDate) {
        return false;
    }

    const start = moment(startDate, DATE_FORMAT).startOf('day');
    const end = moment(endDate, DATE_FORMAT).startOf('day');

    if (!start.isValid() || !end.isValid() || start.isAfter(end)) {
        return false;
    }

    return !end.isAfter(start.clone().add(1, 'month'));
};

const mapRow = (item: Record<string, unknown>, index: number): PendingRetouchTransfersRow => {
    const detailId = Number(pick(item, 'fileTransferRequestDetailId', 'FileTransferRequestDetailId') || 0);
    const barcode = Number(pick(item, 'barcode', 'Barcode') || 0);
    const storedFilename = String(pick(item, 'storedFilename', 'StoredFilename') || '');

    return {
        id: `${detailId}-${barcode}-${storedFilename}-${index}`,
        fileTransferRequestDetailId: detailId,
        fileTransferRequestId: Number(pick(item, 'fileTransferRequestId', 'FileTransferRequestId') || 0),
        barcode,
        specialCode: String(pick(item, 'specialCode', 'SpecialCode') || ''),
        colorCode: String(pick(item, 'colorCode', 'ColorCode') || ''),
        keyCode: String(pick(item, 'keyCode', 'KeyCode') || ''),
        originalFilename: String(pick(item, 'originalFilename', 'OriginalFilename') || ''),
        storedFilename,
        transferDate: formatDisplayDate(pick(item, 'transferDate', 'TransferDate')),
        createdBy: String(pick(item, 'createdBy', 'CreatedBy') || ''),
    };
};

const toExcelRow = (row: PendingRetouchTransfersRow) => ({
    SpecialCode: row.specialCode,
    ColorCode: row.colorCode,
    KeyCode: row.keyCode,
    Barcode: row.barcode,
    StoredFilename: row.storedFilename,
    OriginalFilename: row.originalFilename,
    TransferDate: row.transferDate,
    CreatedBy: row.createdBy,
});

export const PendingRetouchTransfers = () => {
    const { t } = useTranslation();
    const defaults = useMemo(() => getDefaultDateRange(), []);

    const [startDate, setStartDate] = useState<string>(defaults.start);
    const [endDate, setEndDate] = useState<string>(defaults.end);
    const [appliedFilters, setAppliedFilters] = useState<AppliedFilters | null>(null);
    const [hasSearched, setHasSearched] = useState(false);
    const [rows, setRows] = useState<PendingRetouchTransfersRow[]>([]);
    const [totalCount, setTotalCount] = useState(0);
    const [currentPage, setCurrentPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [loading, setLoading] = useState(false);

    const isSearchEnabled = useMemo(
        () => isDateRangeWithinOneMonth(startDate, endDate),
        [startDate, endDate]
    );

    const fetchData = useCallback(async () => {
        if (!appliedFilters) {
            return;
        }

        try {
            setLoading(true);
            const response = await PendingRetouchTransfersQuery.QueryPendingRetouchTransfers({
                ...appliedFilters,
                Page: currentPage + 1,
                PageSize: pageSize,
            });

            if (response?.errors) {
                const errorMessages = Object.values(response.errors).flat().join('\n');
                Swal.fire(errorMessages, '', 'error');
                setRows([]);
                setTotalCount(0);
                return;
            }

            const rawItems = response?.items || response?.Items || [];
            const total = response?.totalCount ?? response?.TotalCount ?? 0;

            setRows(rawItems.map((item: Record<string, unknown>, index: number) => mapRow(item, index)));
            setTotalCount(total);
        } catch {
            Swal.fire({
                icon: 'error',
                title: t('general.error'),
                text: t('studio.pendingRetouchTransfers.fetchError'),
            });
            setRows([]);
            setTotalCount(0);
        } finally {
            setLoading(false);
        }
    }, [appliedFilters, currentPage, pageSize, t]);

    useEffect(() => {
        if (hasSearched && appliedFilters) {
            fetchData();
        }
    }, [hasSearched, appliedFilters, currentPage, pageSize, fetchData]);

    const handleChangeDate = (event: any) => {
        const nextStart = event[0] || '';
        let nextEnd = event[1] || '';

        if (nextStart && nextEnd && !isDateRangeWithinOneMonth(nextStart, nextEnd)) {
            nextEnd = moment(nextStart, DATE_FORMAT).add(1, 'month').format(DATE_FORMAT);
            Swal.fire({
                icon: 'warning',
                title: t('general.warning'),
                text: t('studio.pendingRetouchTransfers.maxOneMonthRange'),
            });
        }

        setStartDate(nextStart);
        setEndDate(nextEnd);
    };

    const handleSearch = useCallback(() => {
        if (!isDateRangeWithinOneMonth(startDate, endDate)) {
            Swal.fire({
                icon: 'warning',
                title: t('general.warning'),
                text: t('studio.pendingRetouchTransfers.maxOneMonthRange'),
            });
            return;
        }

        const startStr = startDate.includes('T') ? startDate : `${startDate}T00:00:00`;
        const endStr = endDate.includes('T') ? endDate : `${endDate}T23:59:59`;

        setCurrentPage(0);
        setHasSearched(true);
        setAppliedFilters({
            TransferDateStart: startStr,
            TransferDateEnd: endStr,
        });
    }, [startDate, endDate, t]);

    const handleExcelExport = async () => {
        if (!appliedFilters) {
            return [];
        }

        const exportPageSize = totalCount > 0 ? totalCount : 10000;
        const response = await PendingRetouchTransfersQuery.QueryPendingRetouchTransfers({
            ...appliedFilters,
            Page: 1,
            PageSize: exportPageSize,
        });

        const rawItems = response?.items || response?.Items || [];
        const excelRows = rawItems
            .map((item: Record<string, unknown>, index: number) => mapRow(item, index))
            .map(toExcelRow);

        if (!excelRows.length) {
            Swal.fire({
                icon: 'info',
                title: t('general.noData'),
                showConfirmButton: false,
                timer: 1500,
            });
            return [];
        }

        downloadExcel({
            excelDownloadData: excelRows,
            excelDownloadFolderName: 'PendingRetouchTransfers',
            fileNameOverride: 'pending-retouch-transfers',
        });

        return [];
    };

    const columns = useMemo(
        () => [
            { field: 'specialCode', headerName: t('searchService.specialCode'), width: 120 },
            { field: 'colorCode', headerName: t('studio.colorCode'), width: 100 },
            { field: 'keyCode', headerName: t('studio.pendingRetouchTransfers.keyCode'), width: 140 },
            { field: 'barcode', headerName: t('studio.barcode'), width: 150 },
            {
                field: 'storedFilename',
                headerName: t('studio.pendingRetouchTransfers.storedFilename'),
                flex: 1,
                minWidth: 280,
            },
            {
                field: 'originalFilename',
                headerName: t('studio.pendingRetouchTransfers.originalFilename'),
                width: 180,
            },
            {
                field: 'transferDate',
                headerName: t('studio.pendingRetouchTransfers.transferDate'),
                width: 170,
            },
            {
                field: 'createdBy',
                headerName: t('studio.pendingRetouchTransfers.createdBy'),
                width: 140,
            },
        ],
        [t]
    );

    return (
        <SCUIContent>
            <SCUIBreadCrumb />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <PhotoFilterOutlinedIcon color="primary" fontSize="large" />
                <Typography variant="h5">{t('studio.pendingRetouchTransfers.title')}</Typography>
            </Box>

            <Paper
                elevation={0}
                sx={{
                    p: 2,
                    borderRadius: '14px',
                    border: '1px solid',
                    borderColor: 'divider',
                }}
            >
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    {t('studio.pendingRetouchTransfers.dateRangeHint')}
                </Typography>
                <Stack
                    direction={{ xs: 'column', lg: 'row' }}
                    spacing={2}
                    alignItems={{ xs: 'stretch', lg: 'center' }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <SCUIDateRangerV2
                            fromDate={startDate}
                            toDate={endDate}
                            setDateRange={handleChangeDate}
                            dateFormat={DATE_FORMAT}
                            size="small"
                            containerStyling={{
                                width: '280px',
                                '& .MuiFormControl-root': { margin: 0 },
                                '& .MuiFormLabel-root': { display: 'none' },
                                '& .MuiOutlinedInput-root': {
                                    height: `${CONTROL_HEIGHT}px`,
                                },
                                '& .MuiInputBase-input': {
                                    padding: '12px 14px',
                                    boxSizing: 'border-box',
                                },
                            }}
                        />
                    </Box>

                    <Button
                        variant="contained"
                        startIcon={<SearchOutlinedIcon />}
                        disabled={!isSearchEnabled}
                        onClick={handleSearch}
                        sx={{
                            height: CONTROL_HEIGHT,
                            minHeight: CONTROL_HEIGHT,
                            maxHeight: CONTROL_HEIGHT,
                            minWidth: 120,
                            borderRadius: '10px',
                            textTransform: 'none',
                            alignSelf: { xs: 'stretch', lg: 'center' },
                        }}
                    >
                        {t('general.search')}
                    </Button>
                </Stack>
            </Paper>

            {hasSearched && (
                <Paper
                    elevation={0}
                    sx={{
                        mt: 2,
                        borderRadius: '14px',
                        border: '1px solid',
                        borderColor: 'divider',
                        p: 2,
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            mb: 1.5,
                            gap: 1,
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <TableRowsOutlinedIcon color="action" />
                            <Typography variant="h6">{t('studio.pendingRetouchTransfers.gridTitle')}</Typography>
                        </Box>
                        <SCUIDownloadButton
                            position="right"
                            onclick={handleExcelExport}
                        />
                    </Box>

                    {loading && <SCUILoadingLinear />}

                    {!loading && rows.length === 0 ? (
                        <SCUINoRecordComponent />
                    ) : (
                        <SCUICustomDataGrid
                            rows={rows}
                            columns={columns}
                            pageSize={pageSize}
                            page={currentPage}
                            rowCount={totalCount}
                            paginationModeChange
                            onPageChange={setCurrentPage}
                            onPageSizeChange={(size: number) => {
                                setPageSize(size);
                                setCurrentPage(0);
                            }}
                            loading={loading}
                        />
                    )}
                </Paper>
            )}
        </SCUIContent>
    );
};
